import { useState } from 'react';
import { AlertCircle, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { motion, AnimatePresence } from 'motion/react';

const RULES = [
    { title: "Be Respectful", description: "Treat everyone with respect. Healthy debates are natural, but kindness and humility is required to create an environment that benefits all parties regardless of opinion, nationality, ethnicity, etc." },
    { title: "No Hate Speech or Bullying", description: "Make sure everyone feels safe. Bullying or harassment of any kind isn't allowed, and degrading comments about things like race, religion, culture and sexual orientation will not be tolerated." },
    { title: "No Excessive Promotion or Spam", description: "The YapTab is for community connection. If you have an interesting project, share it in the corresponding tribe or forum. Personal promotion is not allowed on the YapTab." },
    { title: "No Encouragement of Violence", description: "Any encouragement of violence or violent acts towards any group, person or organization is strictly prohibited. Keep this in mind when making posts or communicating with others." },
    { title: "No Explicit Posting", description: "Adult content (NSFW) is not allowed. Posting explicit material will result in immediate post deletion and account moderation to maintain community integrity." },
    { title: "Respect Privacy & No Doxxing", description: "Mutual trust is essential. Respect everyone's privacy. Doxxing (sharing private info) is not allowed under any circumstances and will result in immediate bans." },
    { title: "Respect Intellectual Property", description: "Only post content you have the right to share. Respect the IP rights of others. Impersonation is strictly forbidden." }
];

function RuleItem({ number, title, description, isOpen, onToggle }: { number: number, title: string, description: string, isOpen: boolean, onToggle: () => void }) {
    return (
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '24px', overflow: 'hidden', marginBottom: '12px' }}>
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between text-left transition-colors"
                style={{ padding: '20px 24px', minHeight: '68px' }}
            >
                <div className="flex items-center" style={{ gap: '16px' }}>
                    <div 
                        className="flex items-center justify-center shrink-0 shadow-lg"
                        style={{ backgroundColor: '#3b82f6', width: '36px', height: '36px', borderRadius: '50%' }}
                    >
                        <span style={{ color: 'white', fontSize: '15px', fontWeight: 'bold' }}>{number}</span>
                    </div>
                    <h3 style={{ color: 'white', fontSize: '16px', fontWeight: 'bold', paddingRight: '12px', lineHeight: '1.4' }}>
                        {title}
                    </h3>
                </div>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="shrink-0"
                >
                    <ChevronDown style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.4)' }} />
                </motion.div>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div style={{ padding: '0 24px 24px 24px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                            {description}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

interface YapTabGuidelinesModalProps {
    isOpen: boolean;
    onAccept: () => void;
}

export function YapTabGuidelinesModal({ isOpen, onAccept }: YapTabGuidelinesModalProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    // onOpenChange is deliberately inert.
    //
    // It used to call onAccept() on ANY close, so anything that dismissed this
    // dialog silently recorded agreement to the community rules — rules the
    // reader may never have seen. Acceptance now happens only where it is
    // actually given: the button at the bottom.
    //
    // Outside clicks and Escape were already prevented on DialogContent, but
    // the built-in close X was not. It was hidden with `[&>button]:hidden`, an
    // arbitrary Tailwind variant absent from the committed pre-compiled
    // index.css — so the class did nothing and the X rendered as a live control
    // in the top-right corner. That is what was being tapped. DialogContent now
    // takes a real `hideClose` prop, which cannot silently stop working.
    return (
        <Dialog open={isOpen} onOpenChange={() => { /* modal, closes only via Accept */ }}>
            <DialogContent 
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                hideClose
                className="p-0 overflow-hidden border border-white/10 rounded-[36px] shadow-2xl outline-none"
                style={{ 
                    zIndex: 100000000, 
                    backgroundColor: '#111111',
                    width: '92vw', 
                    maxWidth: '380px', 
                    maxHeight: '85vh',
                    WebkitTextStroke: '0px transparent', 
                    textShadow: 'none',
                    gap: 0 // Prevent default gap from Radix
                }}
            >
                {/* Use a fixed height container to allow inner scrolling */}
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '85vh', backgroundColor: '#111111' }}>
                    
                    {/* Header */}
                    <div style={{ flexShrink: 0, padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '16px', backgroundColor: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                            <AlertCircle style={{ width: '28px', height: '28px', color: '#3b82f6' }} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', lineHeight: '1.2' }}>
                                Terms of Use & Community Guidelines
                            </h2>
                            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', fontWeight: 'bold', marginTop: '4px' }}>Please agree to the following rules</p>
                        </div>
                    </div>

                    {/* Content (Scrollable) */}
                    <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '24px', backgroundColor: '#111111' }}>
                        {RULES.map((rule, index) => (
                            <RuleItem
                                key={index}
                                number={index + 1}
                                title={rule.title}
                                description={rule.description}
                                isOpen={openIndex === index}
                                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                            />
                        ))}

                        <div style={{ marginTop: '24px', padding: '20px', borderRadius: '24px', backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '20px', lineHeight: '1' }}>⚠️</span>
                            <p style={{ fontSize: '14px', color: 'rgba(252, 211, 77, 0.9)', fontWeight: 'bold', lineHeight: '1.5', margin: 0 }}>
                                Breaking these rules will result in account moderation or deletion.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ flexShrink: 0, padding: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', backgroundColor: '#1a1a1a', borderBottomLeftRadius: '36px', borderBottomRightRadius: '36px' }}>
                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '16px', lineHeight: '1.4' }}>By continuing, you agree to these Terms of Use and acknowledge that objectionable content is prohibited.</p>
                        <Button
                            onClick={onAccept}
                            className="w-full text-white transition-all active:scale-[0.98] border-none"
                            style={{ 
                                height: '56px', 
                                borderRadius: '20px', 
                                fontSize: '18px', 
                                fontWeight: 'bold', 
                                backgroundColor: '#3b82f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                                boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)'
                            }}
                        >
                            <span>I Agree</span>
                            <CheckCircle2 style={{ width: '20px', height: '20px' }} />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
